/**
 * RegistroExito — landing page after MercadoPago approves the payment.
 *
 * MercadoPago redirects here with query params:
 *   ?external_reference={leadId}&payment_id={mpId}&collection_status=approved
 *
 * We call GET /api/auth/activate to verify the payment and get a JWT,
 * then store it and redirect to the app.
 */
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { IconClock, IconCheck, IconAlert } from './components/ui/Icons'

const API = import.meta.env.VITE_API_URL || '/api'

export default function RegistroExito() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState('loading')  // loading | ok | error
  const [msg, setMsg]       = useState('')

  useEffect(() => {
    const ref       = searchParams.get('external_reference')
    const paymentId = searchParams.get('payment_id') || searchParams.get('collection_id')
    const colStatus = searchParams.get('collection_status')

    if (!ref) {
      setStatus('error'); setMsg('No se encontró la referencia del pago.')
      return
    }

    fetch(`${API}/auth/activate?external_reference=${ref}&payment_id=${paymentId ?? ''}&collection_status=${colStatus ?? ''}`)
      .then(async r => ({ ok: r.ok, data: await r.json() }))
      .then(({ data }) => {
        if (data.token) {
          localStorage.setItem('polar_token', data.token)
          localStorage.setItem('polar_user',  JSON.stringify(data.user))
          setStatus('ok')
          setTimeout(() => navigate('/monitor'), 2200)
        } else if (data.alreadyActive) {
          // La cuenta ya está activa: por seguridad no re-emitimos sesión desde la URL
          setStatus('ok'); setMsg('Tu cuenta ya está activa. Te llevamos a iniciar sesión…')
          setTimeout(() => navigate('/login'), 2200)
        } else {
          setStatus('error'); setMsg(data.error || 'No se pudo activar la cuenta.')
        }
      })
      .catch(() => { setStatus('error'); setMsg('Error de red. Intentá de nuevo.') })
  }, [])

  const styles = {
    wrap: { minHeight: '100vh', background: '#0a0a0f', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: '-apple-system,BlinkMacSystemFont,sans-serif' },
    box:  { maxWidth: 420, width: '100%', textAlign: 'center' },
    icon: { fontSize: 56, marginBottom: 16 },
    h1:   { fontSize: 24, fontWeight: 800, color: '#fff', marginBottom: 10 },
    p:    { fontSize: 15, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 },
  }

  if (status === 'loading') return (
    <div style={styles.wrap}>
      <div style={styles.box}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <IconClock size={56} color="#007aff" />
        </div>
        <h1 style={styles.h1}>Activando tu cuenta…</h1>
        <p style={styles.p}>Estamos verificando tu pago con MercadoPago.</p>
      </div>
    </div>
  )

  if (status === 'ok') return (
    <div style={styles.wrap}>
      <div style={styles.box}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <IconCheck size={56} color="#34c759" />
        </div>
        <h1 style={styles.h1}>¡Bienvenido a Manabi!</h1>
        <p style={styles.p}>Tu cuenta fue activada correctamente. Entrando a la app…</p>
      </div>
    </div>
  )

  return (
    <div style={styles.wrap}>
      <div style={styles.box}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <IconAlert size={56} color="#ff3b30" />
        </div>
        <h1 style={{ ...styles.h1, color: '#ff3b30' }}>Algo salió mal</h1>
        <p style={styles.p}>{msg}</p>
        <button
          onClick={() => navigate('/register')}
          style={{ marginTop: 24, background: '#ff3b30', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 28px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Volver al registro
        </button>
      </div>
    </div>
  )
}
